import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import NavBar from '../components/NavBar';
import { DashboardSkeleton } from '../components/SkeletonLoader';
import { useAuth } from '../App';
import apiClient from '../api/client';
import { CATEGORY_COLORS, formatDate } from '../utils/helpers';
import useWindowWidth from '../hooks/useWindowWidth';
import { PRIMARY } from '../styles/shared';
import { EntryIcon, CalendarIcon, ActivityIcon, AwardIcon } from '../components/Icons';
import { EmptyJournalIllustration, OnboardingIllustration } from '../components/Illustrations';
import OnboardingTour, { useOnboardingTour } from '../components/OnboardingTour';
import StreakAndBadges from '../components/StreakAndBadges';
import { trackEvent } from '../utils/analytics';
import { SEMANTIC_COLORS } from '../utils/colors';

// Per-device dedupe for `completed_checkout`. Stripe redirects back to
// /dashboard?subscription=success after a successful subscription. We can't
// reliably gate on a server-side flag from the frontend, so we set a flag
// keyed by sessionId-or-best-effort and skip subsequent fires.
const COMPLETED_CHECKOUT_FIRED_KEY = 'bragsheet_completed_checkout_tracked';

const styles = {
  page: { minHeight: '100vh', backgroundColor: '#f8fafc' },
  content: { maxWidth: '900px', margin: '0 auto', padding: '2rem 1.5rem' },
  welcomeHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
    marginBottom: '2rem',
    flexWrap: 'wrap',
  },
  welcome: {
    fontSize: '1.6rem',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '0.3rem',
  },
  welcomeSub: { color: '#64748b', fontSize: '0.95rem', margin: 0 },
  headerCta: {
    padding: '0.75rem 1.25rem',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '600',
    backgroundColor: PRIMARY,
    color: '#ffffff',
    transition: 'opacity 0.15s',
    minHeight: '44px',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '1rem',
    marginBottom: '2rem',
  },
  // White card with a 4px colored top stripe. The stripe is the semantic
  // signal — orange = streak, teal = entries, etc. Energetic but learnable.
  statCard: (accentColor) => ({
    background: '#ffffff',
    borderRadius: '16px',
    padding: '1.25rem',
    textAlign: 'center',
    boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
    border: '1px solid #e2e8f0',
    borderTop: `4px solid ${accentColor}`,
    // Compensate the extra 3px of top border so card heights match the
    // surrounding 1px-border cards in the same row.
    paddingTop: 'calc(1.25rem - 3px)',
  }),
  statIconCircle: (bgColor) => ({
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: bgColor,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 0.5rem',
  }),
  statNumber: { fontSize: '2rem', fontWeight: '700', color: '#1e293b' },
  statLabel: { fontSize: '0.8rem', color: '#64748b', marginTop: '0.25rem', fontWeight: '500' },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '1rem',
  },
  card: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    padding: '1.25rem',
    marginBottom: '0.75rem',
    cursor: 'pointer',
    transition: 'box-shadow 0.15s',
  },
  entryHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' },
  entryTitle: { fontWeight: '600', color: '#1e293b', fontSize: '0.95rem' },
  entryDate: { fontSize: '0.8rem', color: '#94a3b8' },
  badge: (color) => ({
    display: 'inline-block',
    padding: '0.2rem 0.6rem',
    borderRadius: '999px',
    backgroundColor: color + '1a',
    color: color,
    fontSize: '0.75rem',
    fontWeight: '600',
    marginBottom: '0.4rem',
  }),
  entryPreview: { fontSize: '0.875rem', color: '#64748b', lineHeight: '1.5' },
  actionsRow: {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '2rem',
    flexWrap: 'wrap',
  },
  actionBtn: {
    // M4 (T42): 44px min-height ensures comfortable mobile tap targets.
    padding: '0.75rem 1.25rem',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '600',
    transition: 'opacity 0.15s',
    minHeight: '44px',
  },
  primaryBtn: { backgroundColor: PRIMARY, color: '#ffffff' },
  secondaryBtn: { backgroundColor: '#ffffff', color: PRIMARY, border: `1px solid ${PRIMARY}` },
  emptyState: {
    textAlign: 'center',
    padding: '3rem',
    color: '#64748b',
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    border: '1px solid #e2e8f0',
  },
  loadingText: { textAlign: 'center', color: '#64748b', padding: '2rem' },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' },
  essaySection: { marginTop: '2rem' },
  essayCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    padding: '1.25rem',
    marginBottom: '0.75rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  essayTheme: { fontWeight: '700', color: PRIMARY, fontSize: '1rem', marginBottom: '0.4rem' },
  essayRationale: { fontSize: '0.875rem', color: '#475569', lineHeight: '1.55', marginBottom: '0.5rem' },
  essayChipsRow: { display: 'flex', flexWrap: 'wrap', gap: '0.4rem' },
  essayChip: {
    display: 'inline-block',
    padding: '0.15rem 0.55rem',
    borderRadius: '999px',
    backgroundColor: PRIMARY + '1a',
    color: PRIMARY,
    fontSize: '0.75rem',
    fontWeight: '600',
  },
  essayError: { color: '#dc2626', fontSize: '0.875rem', marginTop: '0.5rem' },
  essayGenerateBtn: {
    padding: '0.75rem 1.25rem',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '600',
    backgroundColor: PRIMARY,
    color: '#ffffff',
    transition: 'opacity 0.15s',
    minHeight: '44px',
  },
};

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { isMobile } = useWindowWidth();
  const [onboardingDismissed, setOnboardingDismissed] = useState(() => localStorage.getItem('bragsheet_onboarding_dismissed') === 'true');
  const { showTour, openTour, closeTour } = useOnboardingTour();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [entriesRes, profileRes] = await Promise.all([
        apiClient.get('/api/entries?page=1&limit=100'),
        apiClient.get('/api/profile'),
      ]);
      setEntries(entriesRes.data.data?.entries || []);
      setProfile(profileRes.data.data);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // completed_checkout: Stripe redirects to /dashboard?subscription=success
  // on successful subscription. Fire once per device (localStorage flag) and
  // strip the query param so a refresh doesn't re-fire even if the flag is
  // wiped. Best-effort — webhook is the source of truth for billing.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('subscription') !== 'success') return;
      const alreadyFired = window.localStorage.getItem(COMPLETED_CHECKOUT_FIRED_KEY) === 'true';
      if (!alreadyFired) {
        window.localStorage.setItem(COMPLETED_CHECKOUT_FIRED_KEY, 'true');
        trackEvent('completed_checkout', { plan: 'pro_monthly' });
      }
      // Clean the query param so a refresh / bookmark doesn't re-fire and
      // doesn't show the success state forever.
      params.delete('subscription');
      const newSearch = params.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
      window.history.replaceState({}, '', newUrl);
    } catch (_) {
      // localStorage disabled / URL parsing failed — best-effort only.
    }
  }, []);

  const now = new Date();
  const thisMonth = entries.filter(e => {
    const d = new Date(e.entryDate || e.createdAt);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });

  const recentEntries = [...entries]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 5);

  const activitiesCount = profile?.activities?.length || 0;
  const awardsCount = profile?.awards?.length || 0;

  const hasProfile = (profile?.activities?.length > 0) || (profile?.awards?.length > 0);
  const hasEntries = entries.length > 0;
  const allComplete = hasProfile && hasEntries;
  const showOnboarding = !onboardingDismissed && !allComplete;

  const dismissOnboarding = () => {
    localStorage.setItem('bragsheet_onboarding_dismissed', 'true');
    setOnboardingDismissed(true);
  };

  return (
    <div style={styles.page}>
      <NavBar />
      {showTour && <OnboardingTour onClose={closeTour} onNavigate={navigate} />}
      {/* M2/M3 (T42): tighten horizontal padding on mobile so cards aren't squished. */}
      <div id="main-content" tabIndex={-1} style={{
        ...styles.content,
        padding: isMobile ? '1.5rem 1rem' : '2rem 1.5rem',
      }}>
        <div style={styles.welcomeHeader}>
          <div>
            <h1 style={styles.welcome}>{(() => {
              const hour = new Date().getHours();
              if (hour < 12) return 'Good morning! \u{1F305}';
              if (hour < 18) return 'Good afternoon! \u{2600}\u{FE0F}';
              return 'Good evening! \u{1F319}';
            })()} {user?.name?.split(' ')[0]}</h1>
            <p style={styles.welcomeSub}>Here's your college journey at a glance</p>
          </div>
          <button
            type="button"
            style={styles.headerCta}
            onClick={() => navigate('/journal/new')}
          >
            + New Entry
          </button>
        </div>

        {loading ? (
          <DashboardSkeleton isMobile={isMobile} />
        ) : error ? (
          <div role="alert" style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#ef4444', borderRadius: '10px', padding: '2rem', textAlign: 'center' }}>
            <p>{error}</p>
            <button onClick={fetchData} style={{ ...styles.actionBtn, ...styles.primaryBtn, marginTop: '1rem' }}>Retry</button>
          </div>
        ) : (
          <>
            {showOnboarding && (
              <div style={{
                backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px',
                padding: '1.5rem', marginBottom: '1.5rem',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h2 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1e293b', margin: 0 }}>Getting Started</h2>
                  <button onClick={dismissOnboarding} aria-label="Dismiss getting started" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '1.2rem', padding: '0.25rem' }}>×</button>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.75rem' }}><OnboardingIllustration size={70} /></div>
                {[
                  { done: hasProfile, label: 'Complete your profile', desc: 'Add activities and awards', path: '/profile' },
                  { done: hasEntries, label: 'Write your first journal entry', desc: 'Document an achievement or reflection', path: '/journal/new' },
                ].map((item, i) => (
                  <div key={i} onClick={() => !item.done && navigate(item.path)} style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 0',
                    borderBottom: i < 1 ? '1px solid #f1f5f9' : 'none',
                    cursor: item.done ? 'default' : 'pointer',
                    opacity: item.done ? 0.6 : 1,
                  }}>
                    <div style={{
                      width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0,
                      backgroundColor: item.done ? '#16a34a' : '#e2e8f0',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#ffffff', fontSize: '0.75rem', fontWeight: '700',
                    }}>{item.done ? '✓' : i + 1}</div>
                    <div>
                      <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.9rem' }}>{item.label}</div>
                      <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <StreakAndBadges
              entries={entries}
              isMobile={isMobile}
              weeklyGoal={profile?.user?.weeklyGoal}
              onEditGoal={async (n) => {
                // Persist server-side via PUT /api/profile (T20). Backend validates 1-10.
                const res = await apiClient.put('/api/profile', { weeklyGoal: n });
                setProfile(res.data?.data || profile);
              }}
            />

            <div style={{
              ...styles.statsRow,
              gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            }}>
              {[
                // Semantic mapping (T03): entries token for both "Total
                // Entries" and "This Month" (both count journal entries),
                // activities token for Activities, awards token for Awards.
                { Icon: EntryIcon, value: entries.length, label: 'Total Entries', token: SEMANTIC_COLORS.entries },
                { Icon: CalendarIcon, value: thisMonth.length, label: 'This Month', token: SEMANTIC_COLORS.entries },
                { Icon: ActivityIcon, value: activitiesCount, label: 'Activities', token: SEMANTIC_COLORS.activities },
                { Icon: AwardIcon, value: awardsCount, label: 'Awards', token: SEMANTIC_COLORS.awards },
              ].map((stat, i) => (
                <div key={i} style={styles.statCard(stat.token.primary)}>
                  <div style={styles.statIconCircle(stat.token.light)}>
                    <stat.Icon size={22} color={stat.token.primary} />
                  </div>
                  <div style={styles.statNumber}>{stat.value}</div>
                  <div style={styles.statLabel}>{stat.label}</div>
                </div>
              ))}
            </div>

            <h2 style={styles.sectionTitle}>Recent Journal Entries</h2>
            {recentEntries.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={{ marginBottom: '1rem' }}><EmptyJournalIllustration size={120} /></div>
                <p style={{ marginBottom: '1rem' }}>No entries yet. Start documenting your journey!</p>
                <button
                  style={{ ...styles.actionBtn, ...styles.primaryBtn }}
                  onClick={() => navigate('/journal/new')}
                >
                  Write First Entry
                </button>
              </div>
            ) : (
              recentEntries.map(entry => (
                <div
                  key={entry.id}
                  style={styles.card}
                  onClick={() => navigate(`/journal/${entry.id}`)}
                >
                  <div style={styles.entryHeader}>
                    <span style={styles.entryTitle}>{entry.title}</span>
                    <span style={styles.entryDate}>{formatDate(entry.entryDate || entry.createdAt)}</span>
                  </div>
                  <div>
                    <span style={styles.badge(CATEGORY_COLORS[entry.category] || '#94a3b8')}>
                      {entry.category}
                    </span>
                  </div>
                  <p style={styles.entryPreview}>
                    {entry.content?.substring(0, 100)}{entry.content?.length > 100 ? '...' : ''}
                  </p>
                </div>
              ))
            )}

            <div style={styles.actionsRow}>
              <button
                style={{ ...styles.actionBtn, ...styles.secondaryBtn }}
                onClick={() => navigate('/journal')}
              >
                View All Entries
              </button>
              <button
                style={{ ...styles.actionBtn, ...styles.secondaryBtn }}
                onClick={() => navigate('/profile')}
              >
                Edit Profile
              </button>
              <button
                style={{ ...styles.actionBtn, ...styles.secondaryBtn }}
                onClick={openTour}
              >
                Show Tour
              </button>
            </div>

            <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
              <Link to="/contact" style={{ color: '#94a3b8', fontSize: '0.85rem', textDecoration: 'none', fontWeight: '500' }}>
                Have suggestions? Send us feedback
              </Link>
            </div>

            <div style={styles.essaySection}>
              <h2 style={styles.sectionTitle}>AI Tools</h2>
              <div style={styles.essayCard}>
                <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '1rem' }}>
                  Essay brainstorming, activity descriptions, auto-tagging, resume bullets, and more — all powered by AI.
                </p>
                <Link
                  to="/ai-tools"
                  style={{
                    ...styles.essayGenerateBtn,
                    display: 'inline-block',
                    textDecoration: 'none',
                    textAlign: 'center',
                  }}
                >
                  Try AI Tools
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
